import { useState, useRef, useEffect } from "react";
import { Link, useLocation, useSearch } from "wouter";
import { Button, Card } from "@/components/ui";
import { BookOpen, Mail, CheckCircle, RefreshCw } from "lucide-react";
import { useAuth } from "@/lib/auth-context";
import { API_BASE } from "@/lib/api";

export default function VerifyEmail() {
  const [, setLocation] = useLocation();
  const search = useSearch();
  const params = new URLSearchParams(search);
  const email = params.get("email") || "";

  const { login: setAuthToken } = useAuth();
  const [code, setCode] = useState(["", "", "", "", "", ""]);
  const [error, setError] = useState("");
  const [success, setSuccess] = useState("");
  const [isVerifying, setIsVerifying] = useState(false);
  const [isResending, setIsResending] = useState(false);
  const [resendCooldown, setResendCooldown] = useState(0);
  const inputs = useRef<(HTMLInputElement | null)[]>([]);

  useEffect(() => {
    inputs.current[0]?.focus();
  }, []);

  useEffect(() => {
    if (resendCooldown > 0) {
      const t = setTimeout(() => setResendCooldown((c) => c - 1), 1000);
      return () => clearTimeout(t);
    }
  }, [resendCooldown]);

  const handleChange = (idx: number, val: string) => {
    const digit = val.replace(/\D/g, "").slice(-1);
    const next = [...code];
    next[idx] = digit;
    setCode(next);
    setError("");
    if (digit && idx < 5) inputs.current[idx + 1]?.focus();
    if (next.every((d) => d) && next.join("").length === 6) {
      submitCode(next.join(""));
    }
  };

  const handleKeyDown = (idx: number, e: React.KeyboardEvent<HTMLInputElement>) => {
    if (e.key === "Backspace" && !code[idx] && idx > 0) {
      inputs.current[idx - 1]?.focus();
    }
  };

  const handlePaste = (e: React.ClipboardEvent) => {
    const pasted = e.clipboardData.getData("text").replace(/\D/g, "").slice(0, 6);
    if (pasted.length === 6) {
      setCode(pasted.split(""));
      setError("");
      inputs.current[5]?.focus();
      submitCode(pasted);
    }
  };

  async function submitCode(codeStr: string) {
    if (!email || codeStr.length !== 6 || isVerifying) return;
    setIsVerifying(true);
    setError("");

    try {
      const res = await fetch(`${API_BASE}/api/auth/verify-email`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ email, code: codeStr }),
      });
      const data = await res.json();

      if (!res.ok) {
        setError(data.message || "Verification failed. Please try again.");
        setCode(["", "", "", "", "", ""]);
        inputs.current[0]?.focus();
        setIsVerifying(false);
        return;
      }

      setAuthToken(data.token);
      setLocation("/dashboard");
    } catch {
      setError("Network error. Please try again.");
      setIsVerifying(false);
    }
  }

  async function handleResend() {
    if (isResending || resendCooldown > 0) return;
    setIsResending(true);
    setError("");
    setSuccess("");

    try {
      const res = await fetch(`${API_BASE}/api/auth/resend-verification`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ email }),
      });
      const data = await res.json();
      setSuccess(data.message || "A new code has been sent.");
      setResendCooldown(60);
      setCode(["", "", "", "", "", ""]);
      inputs.current[0]?.focus();
    } catch {
      setError("Failed to resend. Please try again.");
    } finally {
      setIsResending(false);
    }
  }

  const handleManualSubmit = () => {
    const codeStr = code.join("");
    if (codeStr.length === 6) submitCode(codeStr);
  };

  if (!email) {
    return (
      <div className="min-h-screen flex items-center justify-center p-4 bg-background">
        <Card className="w-full max-w-md p-8 text-center glass-panel">
          <p className="text-muted-foreground mb-4">No email address found. Please register first.</p>
          <Link href="/register">
            <Button>Go to Register</Button>
          </Link>
        </Card>
      </div>
    );
  }

  return (
    <div className="min-h-screen flex flex-col justify-center items-center p-4 bg-background relative overflow-hidden">
      <div className="absolute top-[-10%] left-[-10%] w-96 h-96 bg-primary/10 rounded-full blur-[80px] -z-10" />
      <div className="absolute bottom-[-10%] right-[-10%] w-96 h-96 bg-accent/10 rounded-full blur-[80px] -z-10" />

      <Link href="/">
        <div className="flex items-center gap-3 mb-10 cursor-pointer hover:opacity-80 transition-opacity">
          <div className="w-12 h-12 rounded-xl bg-gradient-to-br from-primary to-accent flex items-center justify-center shadow-lg shadow-primary/20">
            <BookOpen className="w-6 h-6 text-white" />
          </div>
          <span className="font-display font-bold text-3xl tracking-tight text-foreground">Lerni</span>
        </div>
      </Link>

      <Card className="w-full max-w-md p-8 glass-panel">
        <div className="text-center mb-8">
          <div className="w-16 h-16 rounded-2xl bg-primary/10 flex items-center justify-center mx-auto mb-4">
            <Mail className="w-8 h-8 text-primary" />
          </div>
          <h1 className="text-2xl font-bold font-display">Check your email</h1>
          <p className="text-muted-foreground mt-2 text-sm">
            We sent a 6-digit code to
          </p>
          <p className="font-semibold text-foreground text-sm">{email}</p>
        </div>

        {error && (
          <div className="p-3 rounded-xl bg-destructive/10 text-destructive text-sm font-medium text-center mb-5">
            {error}
          </div>
        )}
        {success && (
          <div className="p-3 rounded-xl bg-green-500/10 text-green-600 dark:text-green-400 text-sm font-medium text-center mb-5 flex items-center justify-center gap-2">
            <CheckCircle className="w-4 h-4" />
            {success}
          </div>
        )}

        <div className="flex gap-3 justify-center mb-6" onPaste={handlePaste}>
          {code.map((digit, idx) => (
            <input
              key={idx}
              ref={(el) => { inputs.current[idx] = el; }}
              type="text"
              inputMode="numeric"
              maxLength={1}
              value={digit}
              onChange={(e) => handleChange(idx, e.target.value)}
              onKeyDown={(e) => handleKeyDown(idx, e)}
              className="w-12 h-14 text-center text-2xl font-bold font-mono rounded-xl border border-input bg-background focus:outline-none focus:ring-2 focus:ring-primary focus:border-transparent transition-all"
              disabled={isVerifying}
            />
          ))}
        </div>

        <Button
          className="w-full h-12 text-lg"
          onClick={handleManualSubmit}
          disabled={code.join("").length !== 6 || isVerifying}
        >
          {isVerifying ? "Verifying..." : "Verify Email"}
        </Button>

        <div className="mt-6 text-center">
          <p className="text-sm text-muted-foreground mb-2">Didn't receive the code?</p>
          <button
            onClick={handleResend}
            disabled={isResending || resendCooldown > 0}
            className="text-sm font-semibold text-primary hover:underline disabled:opacity-50 disabled:cursor-not-allowed flex items-center gap-1.5 mx-auto"
          >
            <RefreshCw className={`w-3.5 h-3.5 ${isResending ? "animate-spin" : ""}`} />
            {resendCooldown > 0 ? `Resend in ${resendCooldown}s` : "Resend code"}
          </button>
        </div>

        <p className="mt-6 text-center text-xs text-muted-foreground">
          Wrong email?{" "}
          <Link href="/register" className="text-primary hover:underline font-medium">
            Go back
          </Link>
        </p>
      </Card>
    </div>
  );
}
